import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { chooseAccount, takeLoginHint } from '@higgins/utils';

/**
 * The slice of MSAL's PublicClientApplication this needs — structural, so this
 * package doesn't depend on @azure/msal-browser; each app passes its own
 * instance.
 */
interface MsalApp<A extends { username?: string | null }> {
  initialize(): Promise<void>;
  handleRedirectPromise(): Promise<{ account?: A | null } | null>;
  getAllAccounts(): A[];
  getActiveAccount(): A | null;
  loginRedirect(request: { scopes: string[]; loginHint?: string; prompt?: string }): Promise<void>;
  setActiveAccount(account: A | null): void;
}

/** Entra's account picker — for several cached accounts and nothing to choose by. */
const PROMPT_SELECT_ACCOUNT = 'select_account';

/**
 * Every MSAL app's startup (#242): sign in if needed, as the RIGHT user, then
 * render.
 *
 * Portal tiles pass ?login_hint=<upn>. Handing it to Entra rides the portal's
 * session instead of showing the account picker (measured 2026-09-24: apps
 * that passed it opened without a second prompt, GM Report — which didn't —
 * prompted). Choosing the account it names keeps a shared device from running
 * as the previous user (chooseAccount in @higgins/utils). With no hint (opened
 * directly, or the portal itself) it runs as the account last used here, and
 * with several cached accounts and no such choice it asks (#313).
 */
export async function startMsalApp<A extends { username?: string | null }>(options: {
  msal: MsalApp<A>;
  scopes: string[];
  app: ReactNode;
  rootElementId?: string;
}): Promise<void> {
  const { msal, scopes, app, rootElementId = 'root' } = options;
  const loginHint = takeLoginHint();

  await msal.initialize();
  const redirect = await msal.handleRedirectPromise();

  const accounts = msal.getAllAccounts();
  const account = chooseAccount({
    accounts,
    loginHint,
    redirectAccount: redirect?.account,
    activeAccount: msal.getActiveAccount(),
  });
  if (!account) {
    const mustPick = !loginHint && accounts.length > 1;
    await msal.loginRedirect(mustPick ? { scopes, prompt: PROMPT_SELECT_ACCOUNT } : { scopes, loginHint });
    return;
  }
  msal.setActiveAccount(account);

  createRoot(document.getElementById(rootElementId)!).render(<StrictMode>{app}</StrictMode>);
}
